//Libs
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";

//Local
import { ExistingPurchaseOrder, OPOStatus } from "../../models/purchase-order";
import { ExistingPurchaseOrderReceipt } from "../../models/purchase-order-receipt";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { DollarCurrency } from "../../currency";

interface Props {
  purchaseOrder: ExistingPurchaseOrder;
  receipts: ExistingPurchaseOrderReceipt[];
  onCreateReceipt: () => void;
  onEditReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  onViewReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  onDeleteReceipt: (receipt: ExistingPurchaseOrderReceipt) => void;
  receiptLoading?: boolean;
  onReceiveAll?: () => void; // Optional receive all handler
}

export default function ReceiptsSection(props: Props) {
  const getItemReceivedQuantity = (itemId: string) => {
    return props.receipts.reduce((total, receipt) => {
      const receiptItem = receipt.lineItems.find(
        (item) => item.purchaseOrderItemID === itemId,
      );
      return total + (receiptItem?.quantityReceived || 0);
    }, 0);
  };

  const getReceiptStatusBadge = (receipt: ExistingPurchaseOrderReceipt) => {
    const hasRejectedItems = receipt.lineItems.some(
      (item) => item.quantityRejected > 0,
    );

    if (hasRejectedItems) {
      return (
        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
          Partial/Rejected
        </span>
      );
    }

    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        Received
      </span>
    );
  };

  const hasUnreceivedItems = props.purchaseOrder.lineItems.some((item) => {
    const receivedQty = getItemReceivedQuantity(item.id);
    return receivedQty < item.quantity;
  });

  // Check if there are any items already received
  const hasAnyReceivedItems = props.purchaseOrder.lineItems.some((item) => {
    const receivedQty = getItemReceivedQuantity(item.id);
    return receivedQty > 0;
  });

  // Check if receiving is allowed (not canceled)
  const canReceiveItems = props.purchaseOrder.status !== OPOStatus.CANCELED;

  // Get appropriate button text
  const getReceiveAllButtonText = () => {
    return hasAnyReceivedItems
      ? "Receive All Remaining Items"
      : "Receive All Items";
  };

  return (
    <div className="space-y-6">
      {/* Header with buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Receipts</h2>
          <p className="text-sm text-gray-600">
            Track items received against this purchase order
          </p>
        </div>
        {canReceiveItems && hasUnreceivedItems && (
          <div className="flex gap-3">
            {props.onReceiveAll && (
              <BaseButtonSecondary
                onClick={props.onReceiveAll}
                disabled={props.receiptLoading}
                className="disabled:cursor-not-allowed disabled:opacity-50"
              >
                {getReceiveAllButtonText()}
              </BaseButtonSecondary>
            )}
            <BaseButtonPrimary
              onClick={props.onCreateReceipt}
              disabled={props.receiptLoading}
              className="disabled:cursor-not-allowed disabled:opacity-50"
            >
              Create Receipt
            </BaseButtonPrimary>
          </div>
        )}
        {!canReceiveItems && (
          <div className="text-sm text-red-600">
            Cannot receive items for canceled purchase orders
          </div>
        )}
      </div>

      {/* Receipt List */}
      {props.receipts.length > 0 ? (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Date Received
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Received By
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Items
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {props.receipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {convertToReadableTimestamp(receipt.receivedDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {receipt.receivedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {receipt.totalQuantityReceived} items
                    </td>
                    <td className="px-6 py-4">
                      {getReceiptStatusBadge(receipt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <BaseButtonSecondary
                          onClick={() => props.onEditReceipt(receipt)}
                          className="flex items-center gap-1 text-sm"
                        >
                          <PencilIcon className="h-4 w-4" />
                          Edit
                        </BaseButtonSecondary>
                        <BaseButtonSecondary
                          onClick={() => props.onDeleteReceipt(receipt)}
                          className="flex items-center gap-1 border-red-300 text-sm text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Delete
                        </BaseButtonSecondary>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-8 text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No receipts yet
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a receipt for received items.
          </p>
        </div>
      )}
    </div>
  );
}
